// Payment terms and the client's invoice table (PRD CF1–CF3, rev. 1.7; Chris, 2026-10-23). Pure rules,
// shared by the API and the browser.
//
// A client is set up once with its payment terms (net days, Net 45 by default) and an invoice table:
// on which day each service is invoiced. 3Seven always bills in advance, and the day comes from the
// contract: a contract dated the 12th invoices on the 12th, unless the table gives a service its own
// day. A project takes its client's table, and any payment line can set its own day. Expected
// receipt = invoice date + the client's net days; only the cashflow view uses it (CF3), so project
// margin and cash by month stay on the month earned.

import { addDays, daysInMonth, isIsoDate, type Iso, type Month } from "./dates";
import { REVENUE_TYPES } from "./projects";
import { expandTiers, type RevenueType } from "./revenue";

export const DEFAULT_TERMS_DAYS = 45;

/** When in the invoice month an invoice goes out: the contract date's day, the last day, or a fixed day. */
export type InvoiceDay = "contract" | "last" | number;

/** One row of a client's invoice table: a service (revenue type) and its invoice day. */
export interface InvoiceRule {
  type: RevenueType;
  day: InvoiceDay;
  note?: string;
}

/** What a client record carries for billing (CF1–CF2). */
export interface ClientBilling {
  /** Net days from invoice to payment. */
  termsDays: number;
  rules: InvoiceRule[];
  notes?: string;
}

export const defaultBilling = (): ClientBilling => ({ termsDays: DEFAULT_TERMS_DAYS, rules: [] });

export const isInvoiceDay = (v: unknown): v is InvoiceDay => v === "contract" || v === "last" || (Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 31);

export function invoiceDayLabel(day: InvoiceDay, contractDate?: Iso): string {
  if (day === "last") return "Last day of the month";
  if (day === "contract") return contractDate ? `Contract date's day (the ${ordinal(Number(contractDate.slice(8, 10)))})` : "Contract date's day";
  return `The ${ordinal(day)}`;
}

export function ordinal(n: number): string {
  const s = n % 100 >= 11 && n % 100 <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${s}`;
}

export function validateBilling(b: Partial<ClientBilling>): string[] {
  const errors: string[] = [];
  if (!Number.isInteger(b.termsDays) || (b.termsDays as number) < 0 || (b.termsDays as number) > 180) errors.push("Payment terms are 0 to 180 days.");
  if (!Array.isArray(b.rules)) return [...errors, "The invoice table is incomplete."];
  const seen = new Set<string>();
  for (const r of b.rules) {
    if (!REVENUE_TYPES.includes(r?.type)) errors.push("Pick the service each row of the invoice table is for.");
    else if (seen.has(r.type)) errors.push("Each service has one row in the invoice table.");
    else seen.add(r.type);
    if (!isInvoiceDay(r?.day)) errors.push("An invoice day is the contract date's day, the last day, or a day from 1 to 31.");
    if (r?.note !== undefined && (typeof r.note !== "string" || r.note.length > 200)) errors.push("A note in the invoice table is up to 200 characters.");
  }
  if (b.notes !== undefined && (typeof b.notes !== "string" || b.notes.length > 2_000)) errors.push("Billing notes are up to 2,000 characters.");
  return [...new Set(errors)];
}

/** The date in a month for an invoice day; a day past the month's end is its last day. No contract date: the 1st. */
export function invoiceDateIn(month: Month, day: InvoiceDay, contractDate?: Iso): Iso {
  const last = daysInMonth(month);
  const d = day === "last" ? last : day === "contract" ? (contractDate && isIsoDate(contractDate) ? Number(contractDate.slice(8, 10)) : 1) : day;
  return `${month}-${String(Math.min(d, last)).padStart(2, "0")}`;
}

/** Where a line's invoice day came from, to show on the schedule. */
export type InvoiceDaySource = "line" | "client" | "contract" | "default";

/** The day a line invoices on: its own, else the client's row for its service, else the contract date's day. */
export function resolveInvoiceDay(line: { type?: RevenueType; invoiceDay?: InvoiceDay }, billing: ClientBilling | undefined, contractDate?: Iso): { day: InvoiceDay; source: InvoiceDaySource } {
  if (line.invoiceDay !== undefined) return { day: line.invoiceDay, source: "line" };
  const rule = billing?.rules.find((r) => r.type === (line.type ?? "client-fee"));
  if (rule) return { day: rule.day, source: "client" };
  return { day: "contract", source: contractDate ? "contract" : "default" };
}

export interface ScheduledInvoice {
  lineId: string;
  label?: string;
  type: RevenueType;
  amount: number;
  invoiceMonth: Month;
  invoiceDate: Iso;
  /** Invoice date + the client's net days (CF3). */
  expectedReceipt: Iso;
  serviceStart: Iso;
  serviceEnd: Iso;
  daySource: InvoiceDaySource;
  /** The net days used: the client's, or the partner's for partner funding and referrals. */
  termsDays: number;
  /** Paid by the partner rather than the client. */
  partner: boolean;
}

/**
 * Every invoice a project's schedule produces, dated (CF2) with its expected receipt (CF3). Reimbursable
 * lines are invoiced like any other: the client pays them back on the same terms.
 */
export function scheduledInvoices(
  schedule: readonly { id: string; label?: string; type?: RevenueType; invoiceDay?: InvoiceDay; amount: number; frequency: "once" | "monthly" | "quarterly"; count: number; firstInvoiceMonth: Month; monthsCovered: number }[],
  billing: ClientBilling | undefined,
  contractDate?: Iso,
  /** Net days for revenue a partner pays (partner funding, referrals), from the cash settings. */
  partnerTerms?: Partial<Record<RevenueType, number>>,
): ScheduledInvoice[] {
  const clientTerms = billing?.termsDays ?? DEFAULT_TERMS_DAYS;
  const out: ScheduledInvoice[] = [];
  for (const line of schedule) {
    const { day, source } = resolveInvoiceDay(line, billing, contractDate);
    const type = line.type ?? "client-fee";
    const partner = type === "partner-funding" || type === "referral";
    const terms = partner ? (partnerTerms?.[type] ?? DEFAULT_TERMS_DAYS) : clientTerms;
    for (const p of expandTiers([line])) {
      const invoiceDate = invoiceDateIn(p.invoiceMonth, day, contractDate);
      out.push({ lineId: line.id, label: line.label, type: p.type, amount: p.amount, invoiceMonth: p.invoiceMonth, invoiceDate, expectedReceipt: addDays(invoiceDate, terms), serviceStart: p.serviceStart, serviceEnd: p.serviceEnd, daySource: source, termsDays: terms, partner });
    }
  }
  return out.sort((a, b) => a.invoiceDate.localeCompare(b.invoiceDate) || a.lineId.localeCompare(b.lineId));
}
